import { Button } from "antd";
import { useEffect, useState } from "react";
import axios from "axios";
import { fetchPatientAuthSamples, removePatientAuthSamples } from "../../../api/geneCodes/AuthSamples";
import { createOrUpdateSamplesPanels } from "../../../api/geneCodes/PanelApi";
import { fetchSample } from "../../../api/labiq/SampleApi";
import { Sample } from "../../../types/lab/SampleTypes";
import { PlusOutlined, GlobalOutlined, DeleteOutlined } from "@ant-design/icons";
import DataTable from "../../shared/DataTable";
import InlineLoading from "../../shared/InlineLoading";
import SampleModal from "../../../pages/lab/tracking/components/SampleModal";
import AssignSampleModal from "../../../pages/healthcare/patient/modalComponents/submodals/AssignSampleModal";
import GlobalPanelModal from "./GlobalPanelModal";
import { User } from "../../../types/admin/UserTypes";

interface SampleTabProps {
	patient: User;
	isSubmitting: boolean;
	setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
	setErrorMessages: React.Dispatch<React.SetStateAction<any[]>>;
	handleAutoSave: () => void;
}

const SampleTab: React.FC<SampleTabProps> = ({ patient, isSubmitting, setIsSubmitting, setErrorMessages }) => {
	const [samples, setSamples] = useState<Sample[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [sampleModalOpen, setSampleModalOpen] = useState(false);
	const [globalPanelsOpen, setGlobalPanelsOpen] = useState(false);
	const [selectedSample, setSelectedSample] = useState<Sample | null>(null);
	const [isAssignSampleModalOpen, setIsAssignSampleModalOpen] = useState(false);

	useEffect(() => {
		if (patient.id !== "") fetchPatientSample();
	}, [patient.id]);

	const fetchPatientSample = async () => {
		setIsLoading(true);
		try {
			const result = await fetchPatientAuthSamples(patient.email);
			const loadedSamples: Sample[] = result.map((s: any) => ({
				id: s.id,
				authSampleId: s.idauth_samples,
				is_active: s.sample_activation,
				sampleID: s.sampleID,
				qsBases: [],
				created_at: new Date().toISOString(),
				updated_at: new Date().toISOString(),
				sampleOA: null,
				client: "temp",
			}));
			setSamples(loadedSamples);
		} catch (error) {
			handleErrors(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleErrors = (error: unknown) => {
		if (axios.isAxiosError(error) && error.response) {
			if (error.response.data?.errors) {
				const msgs = Object.entries(error.response.data.errors).flatMap(([_, m]) => m as string[]);
				setErrorMessages(msgs);
			} else if (error.response.data?.message) {
				setErrorMessages([error.response.data.message]);
			} else {
				setErrorMessages(["An unknown error occurred."]);
			}
		} else if (error instanceof Error) {
			setErrorMessages([error.message]);
		} else {
			setErrorMessages(["An unknown error occurred."]);
		}
	};

	const removePatientFromSample = async (sampleId: number, authSampleId: number) => {
		setIsSubmitting(true);
		try {
			await createOrUpdateSamplesPanels(authSampleId, []);
			await removePatientAuthSamples(authSampleId);
			await fetchPatientSample();
		} catch (error) {
			handleErrors(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const openSampleModal = async (selectedSample: Sample) => {
		setIsLoading(true);
		const sample = await fetchSample(selectedSample.sampleID);
		sample.authSampleId = selectedSample.authSampleId;
		sample.is_active = selectedSample.is_active;
		setSelectedSample(sample);
		setSampleModalOpen(true);
		setIsLoading(false);
	};

	const columns = [
		{ title: "Sample ID", dataIndex: "sampleID", key: "sampleID" },
		{
			title: "Lab Status",
			dataIndex: ["sampleOA", "status"],
			key: "status",
			render: (status: string) => status || "N/A",
		},
		{
			title: "Actions",
			key: "actions",
			width: 100,
			render: (_: any, record: Sample) => (
				<Button
					type="text"
					danger
					icon={<DeleteOutlined />}
					onClick={(e) => {
						e.stopPropagation();
						removePatientFromSample(record.id, record.authSampleId || 0);
					}}
					loading={isSubmitting}
				/>
			),
		},
	];

	if (isLoading) return <InlineLoading minHeight={300} message="Loading samples…" />;

	return (
		<div>
			<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
				<Button icon={<GlobalOutlined />} onClick={() => setGlobalPanelsOpen(true)}>
					Global Panels
				</Button>
				<Button type="primary" icon={<PlusOutlined />} onClick={() => setIsAssignSampleModalOpen(true)}>
					Assign Test
				</Button>
			</div>

			<DataTable
				bare
				columns={columns}
				dataSource={samples}
				rowKey={(r: Sample) => r.id}
				onRowClick={openSampleModal}
				emptyTitle="No samples found"
				emptyDescription="Assign a test to link a sample to this user"
				pagination={{ pageSize: 10 }}
			/>

			{selectedSample && sampleModalOpen && (
				<SampleModal
					isOpen={sampleModalOpen}
					onClose={() => { setSampleModalOpen(false); fetchPatientSample(); }}
					sample={selectedSample}
					patient={patient as any}
					onFormSubmission={() => { setSampleModalOpen(false); fetchPatientSample(); }}
				/>
			)}

			{isAssignSampleModalOpen && (
				<AssignSampleModal
					isOpen={isAssignSampleModalOpen}
					onClose={() => setIsAssignSampleModalOpen(false)}
					patient={patient as any}
					fetchPatientSample={fetchPatientSample}
					handleErrors={handleErrors}
				/>
			)}

			{globalPanelsOpen && (
				<GlobalPanelModal
					isOpen={globalPanelsOpen}
					onClose={() => setGlobalPanelsOpen(false)}
					user={patient}
				/>
			)}
		</div>
	);
};

export default SampleTab;
